import React from 'react';
import type { Maybe } from '../../../../utils/typings';
import { FieldDatatype } from '../../datatype/Datatype';
import { EvaluationScope } from '../../evaluate/EvaluationScope';
import { IReferenceObjectValue } from '../../references/IReference';

/** Utilizza un tipo di dato statico, es. una lunghezza numerica */
interface IConditionOperatorArgumentStaticValue {
  type: 'staticValue';
  field: FieldDatatype;
}

/** Utilizza lo stesso tipo di dato del widget */
interface IConditionOperatorArgumentReferenceValue {
  type: 'referenceValue';
  isArray?: boolean;
}

/** Utilizza una struttura di matching sull'array, ovvero un SubconditionsGroup */
interface IConditionOperatorArgumentArrayMatching {
  type: 'arrayMatching';
}

// TODO Identificare se questo operatore prende lo stesso tipo di dati della
// Reference o se è un tipo diverso (es. lunghezza di una stringa)
export type IConditionOperatorArgument =
  | IConditionOperatorArgumentStaticValue
  | IConditionOperatorArgumentReferenceValue
  | IConditionOperatorArgumentArrayMatching;

type MapArgumentType<T extends IConditionOperatorArgument> =
  T extends IConditionOperatorArgumentStaticValue
    ? Maybe<number>
    : T extends IConditionOperatorArgumentReferenceValue
    ? Maybe<number>
    : // Ritorna l'insieme di oggetti che _verificano_ la condizione specificata
    // nella Subconditions. È poi l'operatore a decidere come confrontarli
    T extends IConditionOperatorArgumentArrayMatching
    ? Maybe<IReferenceObjectValue[]>
    : never;

type MapArgumentsType<T extends readonly IConditionOperatorArgument[]> = {
  [K in keyof T]: MapArgumentType<T[K]>;
};

export interface IBaseConditionOperatorType<
  TCode extends string = string,
  TArgs extends readonly IConditionOperatorArgument[] = readonly IConditionOperatorArgument[],
> {
  code: TCode;
  label: string;
  description: string;
  allowsField: (field: FieldDatatype) => boolean;
  icon?: React.ReactNode;
  args: TArgs;
  compare(
    value: any,
    args: MapArgumentsType<TArgs>,
    scope: EvaluationScope,
  ): boolean;
}

export function createConditionOperator<
  const TCode extends string,
  const TArgs extends readonly IConditionOperatorArgument[],
  const TOperator extends IBaseConditionOperatorType<TCode, TArgs>,
>(operator: TOperator): TOperator {
  return operator;
}
